// filepath: apps/web/src/admin/people/edit.tsx
//
// ISS-518 — PersonEditScreen (PATCH /api/people/{id}).
//
// INV-7: the row_version from the latest GET response is carried
// in the PATCH body; the server rejects stale submissions with
// 409 + code='row_version_mismatch' -- the SPA surfaces a
// StaleVersionError with a Reload button.
//
// INV-8: a fresh X-Idempotency-Key per submit so a double-submit
// is replayed as 200 rather than producing a duplicate audit
// row.
//
// ISS-431-uatrzt2 React crash fix: the project opts out of
// react-router-dom (FR-UI-CABINET-1 AC-9); uses the project's
// own navigate() primitive.

import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { StaleVersionError } from "@/design-system/error-states/StaleVersionError";
import { PermissionDenied } from "@/design-system/error-states/PermissionDenied";
import { ApiError } from "@/definitions/types";
import { navigate } from "@/features/cabinet/usePathname";
import { PersonForm, type PersonFormValues } from "./PersonForm";
import { getPerson, newIdempotencyKey, updatePerson } from "./api";
import type { Person } from "@/features/directory/types";

function isPermissionDenied(e: unknown): boolean {
  if (e instanceof ApiError) return e.status === 403 || e.status === 404;
  return false;
}

function isRowVersionConflict(e: unknown): boolean {
  if (e instanceof ApiError) {
    return e.status === 409 && e.code === "row_version_mismatch";
  }
  return false;
}

export interface PersonEditScreenProps {
  id?: string;
  loader?: (id: string) => Promise<Person>;
  submit?: (id: string, input: UpdatePersonArgs, key: string) => Promise<void>;
}

type UpdatePersonArgs = Parameters<typeof updatePerson>[1];

export function PersonEditScreen({
  id = "",
  loader,
  submit,
}: PersonEditScreenProps = {}): React.ReactElement {
  const { t } = useTranslation();
  const [row, setRow] = useState<Person | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [permissionDenied, setPermissionDenied] = useState<boolean>(false);
  const [stale, setStale] = useState<boolean>(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    setPermissionDenied(false);
    setStale(false);
    try {
      const l = loader ?? ((pid: string) => getPerson(pid));
      const r = await l(id);
      setRow(r);
    } catch (e) {
      if (isPermissionDenied(e)) {
        setPermissionDenied(true);
      } else {
        setError(e instanceof Error ? e.message : String(e));
      }
    } finally {
      setLoading(false);
    }
  }, [id, loader]);

  useEffect(() => {
    void load();
  }, [load]);

  const onSubmit = useCallback(
    async (values: PersonFormValues) => {
      if (!row) return;
      setSubmitting(true);
      setError(null);
      setStale(false);
      try {
        const key = newIdempotencyKey();
        const input: UpdatePersonArgs = {
          display_name: values.display_name,
          email: values.email === "" ? null : values.email,
          phone: values.phone === "" ? null : values.phone,
          status: values.status,
          row_version: row.row_version,
        };
        const submitFn = submit ?? updatePerson;
        await submitFn(row.id, input, key);
        navigate(`/admin/people/${encodeURIComponent(row.id)}`);
      } catch (e) {
        if (isPermissionDenied(e)) {
          setPermissionDenied(true);
        } else if (isRowVersionConflict(e)) {
          setStale(true);
        } else {
          setError(e instanceof Error ? e.message : String(e));
        }
      } finally {
        setSubmitting(false);
      }
    },
    [row, submit],
  );

  if (loading) {
    return (
      <p data-testid="person-edit-loading">
        {t("directory.people.edit.loading", "Loading person…")}
      </p>
    );
  }
  if (permissionDenied) {
    return (
      <div data-testid="person-edit-permission-denied">
        <PermissionDenied />
      </div>
    );
  }
  if (error && !row) {
    return (
      <p role="alert" data-testid="person-edit-error">
        {t("directory.people.edit.error", "Error: {{message}}", {
          message: error,
        })}
      </p>
    );
  }
  if (!row) {
    return (
      <p data-testid="person-edit-not-found">
        {t("directory.people.edit.not_found", "Person not found")}
      </p>
    );
  }

  return (
    <>
      {stale ? (
        <StaleVersionError
          entityName={row.display_name ?? row.id}
          onReload={() => void load()}
          data-testid="person-edit-stale"
        />
      ) : null}
      <PersonForm
        // ISS-805 — PersonForm tracks each field's dirty state, so pointing
        // the route at a different person must NOT carry this person's
        // in-progress edits into the next form. `key` remounts the form on a
        // row change, which is the form's "different record" path (it takes
        // no id prop). A reload of the SAME row keeps the key, so a 409
        // row_version conflict still leaves the operator's text in place.
        key={row.id}
        heading={t("directory.people.edit.title", "Edit person")}
        initial={{
          display_name: row.display_name ?? "",
          email: row.email ?? "",
          phone: row.phone ?? "",
          status: row.status ?? "active",
        }}
        onSubmit={onSubmit}
        onCancel={() => navigate(`/admin/people/${encodeURIComponent(row.id)}`)}
        submitting={submitting}
        error={error}
        submitLabel={t("directory.people.edit.submit", "Save")}
        loadingLabel={t("directory.people.edit.submitting", "Saving…")}
      />
    </>
  );
}

export default PersonEditScreen;
